'use client';

import {Breadcrumb} from '@/modules/foundation/components/breadcrumb';
import {Container} from '@/modules/foundation/components/container';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {PortableText} from '@/modules/foundation/sanity/portable-text';
import {TextHeading} from '@/modules/page/components/page-head/text-heading';

import {getAdapter} from '../../configuration';
import {useProductForm} from '../../hooks/useProductForm';
import {useProductPage} from '../../hooks/useProductPage';
import {ProductForm} from '../../ProductForm';
import {ProductImageCarousel} from '../../ProductImageCarousel';
import {STICKER_LOCATION_OUTSIDE} from '../configuration/stickerLocationConfiguration';
import {validateStickerConfiguration} from '../configuration/stickerProductConfiguration';
import type {StickerProductFragment} from '../query';

import {
	StickerColorSelector,
	StickerElementSelector,
	StickerLocationSelector,
	StickerVariantSelector,
} from './StickerSelectors';

export type StickerPageProps = {
	fragment: StickerProductFragment;
	shoppingDisabled?: boolean;
};

export function StickerProductPage({fragment, shoppingDisabled}: StickerPageProps) {
	const {isValid, breadcrumbs, configuration, priceIncVat, selectedItems} = useProductPage({
		fragment,
		validate: validateStickerConfiguration,
		adapter: getAdapter(fragment),
	});

	const {action, onChange, cart, cartLineId} = useProductForm(fragment);

	return (
		<>
			<TextHeading
				heading={fragment.name}
				breadcrumb={<Breadcrumb breadcrumb={breadcrumbs} />}
			>
				<Paragraph>{fragment.supplier?.name}</Paragraph>
			</TextHeading>
			<Container width="normal" spacingBottom="normal" spacingTop="normal">
				<div className="md:flex md:flex-row md:items-start md:gap-8">
					{/*<JsonLd jsonLd={productData} />*/}
					<div className="mb-8 md:sticky md:top-8 md:w-1/2">
						<ProductImageCarousel product={fragment} config={configuration} />
					</div>

					<div className="md:w-1/2">
						<ProductForm
							action={action}
							isValid={isValid}
							shoppingDisabled={shoppingDisabled}
							onChange={onChange}
							cart={cart}
							cartLineId={cartLineId}
							selectedItems={selectedItems}
							priceIncVat={priceIncVat}
						>
							<input type="hidden" name="productId" defaultValue={fragment._id} />

							{fragment.description && (
								<div className="mb-8">
									<PortableText value={fragment.description} />
								</div>
							)}

							<StickerVariantSelector
								variants={fragment.variants ?? []}
								defaultVariantId={configuration?.variantId ?? undefined}
							/>
							<StickerLocationSelector
								fragment={fragment}
								defaultLocation={configuration.location ?? STICKER_LOCATION_OUTSIDE}
							/>
							<StickerColorSelector
								fragment={fragment}
								defaultColorId={configuration.colorId}
							/>
							<StickerElementSelector fragment={fragment} config={configuration} />
						</ProductForm>
					</div>
				</div>
			</Container>
		</>
	);
}
